import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { IFormProfile } from 'legacy/lut/classes/models/IFormProfile'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { CrudStore } from 'core/types/crudStore'
import { LNRFormCacheKey } from 'onboarding/api/lnrFormApi'
import { parseThirdPartyValue } from 'onboarding/classes/helpers/parseThirdPartyValue'
import { TPartnerAdditionalInfo } from 'onboarding/classes/models/partnerAdditionalInfo'
import { useParentRequest } from 'onboarding/hooks/useParentRequest'
import { useRequest } from 'onboarding/hooks/useRequest'
import { contactService } from 'onboarding/services/contactService'
import { lnrFormService } from 'onboarding/services/lnrFormService'

import { getEmptyLNRForm } from '../helpers'
import { TLNRForm } from '../models'

interface IProps {
  initLNRFormData: (value: TLNRForm) => void
  requestId: string
  contactId: string
  startDate?: string
}

export const useLNRData = ({ initLNRFormData, requestId, contactId, startDate }: IProps) => {
  const { req } = useRequest(requestId)
  const { parentRequest } = useParentRequest(req.Parent)

  const [isLNRFormFetching, setIsLNRFormFetching] = useState(false)
  const [isContactFetching, setIsContactFetching] = useState(false)
  const [contact, setContact] = useState<IFormProfile | null>(null)

  const contactModel = contact ? new DataModel(contact) : null
  const resetContact = useCallback(() => {
    setContact(null)
  }, [])

  const [isDisabled, setIsDisabled] = useState(false)
  const seqCrud = CrudStore.get(CrudStoreItem.SecurimatePartnersFetch, 'lnr data')
  const request = useMemo(() => parentRequest ?? req, [req, parentRequest])

  const fetchContact = useCallback(async (contactId: string) => {
    setIsContactFetching(true)
    const contact = await contactService.fetchByContactId(contactId, 'lnrForm')
    setContact(contact)
    setIsContactFetching(false)
  }, [])

  useEffect(() => {
    const fetch = async () => {
      if (request.EntryId) {
        setIsLNRFormFetching(true)
        const lnrFormData = await lnrFormService.fetchByRequestId(
          request.EntryId,
          LNRFormCacheKey.lnrForm
        )
        if (lnrFormData) {
          await handleThirdPartyDDIQ(lnrFormData.ContactId || '')
          initLNRFormData(lnrFormData)
        } else if (startDate) {
          initLNRFormData({ ...getEmptyLNRForm(), SentToCompliance: startDate })
        }
        setIsLNRFormFetching(false)
      }
    }

    const handleThirdPartyDDIQ = async (contactId: string) => {
      if (request.PartnerId) {
        const res: TPartnerAdditionalInfo | null = await seqCrud.getById({
          id: request.PartnerId,
          force: true,
          keyField: 'PartnerID',
        })
        const thirdParty = res?.DDIQ ? parseThirdPartyValue(res.DDIQ) : null
        if (res && thirdParty && thirdParty.id && contactId && !isDisabled) {
          setIsDisabled(true)
        } else {
          setIsDisabled(false)
        }
      }
    }

    fetch()
    return lnrFormService.getCrud(LNRFormCacheKey.lnrForm).subscribe('change', () => {
      fetch()
    })
  }, [request.EntryId, request.PartnerId])

  useEffect(() => {
    if (contactId) {
      fetchContact(contactId)
    }
  }, [contactId, fetchContact])

  return {
    contact: contactModel,
    isLNRFormFetching,
    isContactFetching,
    resetContact,
    partnerId: request.PartnerId,
    refetchContact: fetchContact,
    isDisabled,
  }
}
